// @ts-ignore - jsPDF types may not be available
import jsPDF from 'jspdf';

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  jerseyNumber?: number;
  position?: string;
  isActive: boolean;
  canPlay: boolean;
  statusReasons: string[];
}

interface MatchInfo {
  homeTeam: { id: string; name: string };
  awayTeam: { id: string; name: string };
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  league?: { name: string };
}

export const generateMatchRosterPDF = async (
  matchInfo: MatchInfo,
  homeTeamPlayers: Player[],
  awayTeamPlayers: Player[],
  teamName: 'home' | 'away',
  addEarlyWatermark: boolean = false
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  let yPos = margin;
  
  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Match Roster', margin, yPos);
  yPos += 10;
  
  // Match Information
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${matchInfo.homeTeam.name} vs ${matchInfo.awayTeam.name}`, margin, yPos);
  yPos += 6;
  
  if (matchInfo.league) {
    doc.setFontSize(10);
    doc.text(matchInfo.league.name, margin, yPos);
    yPos += 6;
  }
  
  doc.text(`Date: ${new Date(matchInfo.scheduledDate).toLocaleDateString()}`, margin, yPos);
  yPos += 6;
  doc.text(`Time: ${matchInfo.scheduledTime}`, margin, yPos);
  yPos += 6;
  doc.text(`Location: ${matchInfo.location}`, margin, yPos);
  yPos += 10;
  
  // Team Name
  const selectedTeam = teamName === 'home' ? matchInfo.homeTeam : matchInfo.awayTeam;
  const selectedPlayers = teamName === 'home' ? homeTeamPlayers : awayTeamPlayers;
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(selectedTeam.name, margin, yPos);
  yPos += 8;
  
  // Legend
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 0, 0);
  doc.text('X', margin + 5, yPos);
  doc.setTextColor(0, 0, 0);
  doc.text(' = Not Eligible', margin + 8, yPos);
  yPos += 10;
  
  // Players Grid
  const playersPerRow = 3;
  const cardWidth = (contentWidth - (playersPerRow - 1) * 5) / playersPerRow;
  const cardHeight = 50;
  const imageSize = 30;
  const startX = margin;
  let currentX = startX;
  
  // Filter to only active players
  const activePlayers = selectedPlayers.filter(p => p.isActive);
  
  activePlayers.forEach((player, index) => {
    // Check if we need a new page
    if (yPos + cardHeight > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      currentX = startX;
    }
    
    // Check if we need a new row
    if (index > 0 && index % playersPerRow === 0) {
      yPos += cardHeight + 5;
      currentX = startX;
    }
    
    // Player card background
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(255, 255, 255);
    doc.rect(currentX, yPos, cardWidth, cardHeight, 'FD');
    
    // Profile picture placeholder (circle)
    const imageX = currentX + (cardWidth / 2);
    const imageY = yPos + 8;
    doc.setFillColor(220, 220, 220);
    doc.circle(imageX, imageY, imageSize / 2, 'F');
    
    // Player initials in circle
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    const initials = `${player.firstName[0]}${player.lastName[0]}`.toUpperCase();
    const textWidth = doc.getTextWidth(initials);
    doc.text(initials, imageX - textWidth / 2, imageY + 3);
    doc.setTextColor(0, 0, 0);
    
    // Red X if not eligible
    if (!player.canPlay) {
      doc.setDrawColor(255, 0, 0);
      doc.setLineWidth(2);
      const xSize = 8;
      const xX = imageX + imageSize / 2 - xSize / 2;
      const xY = imageY - imageSize / 2;
      doc.line(xX, xY, xX + xSize, xY + xSize);
      doc.line(xX + xSize, xY, xX, xY + xSize);
      doc.setLineWidth(0.5);
    }
    
    // Player name
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const nameText = `${player.firstName} ${player.lastName}`;
    const nameWidth = doc.getTextWidth(nameText);
    doc.text(nameText, currentX + (cardWidth / 2) - (nameWidth / 2), yPos + imageSize + 12);
    
    // Jersey number
    if (player.jerseyNumber) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`#${player.jerseyNumber}`, currentX + 5, yPos + imageSize + 18);
    }
    
    // Position
    if (player.position) {
      doc.setFontSize(8);
      doc.text(player.position, currentX + cardWidth - 5, yPos + imageSize + 18, { align: 'right' });
    }
    
    // Status reasons (if not eligible)
    if (!player.canPlay && player.statusReasons.length > 0) {
      doc.setFontSize(7);
      doc.setTextColor(255, 0, 0);
      const reasons = player.statusReasons.join(', ');
      const maxWidth = cardWidth - 10;
      const splitReasons = doc.splitTextToSize(reasons, maxWidth);
      doc.text(splitReasons, currentX + 5, yPos + imageSize + 25, { maxWidth });
      doc.setTextColor(0, 0, 0);
    }
    
    currentX += cardWidth + 5;
  });
  
  // Add "EARLY" watermark if needed
  if (addEarlyWatermark) {
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Save current state
      const currentTextColor = doc.getTextColor();
      const currentFontSize = doc.getFontSize();
      
      // Set watermark properties (light gray for watermark effect)
      doc.setTextColor(200, 200, 200); // Light gray
      doc.setFontSize(80);
      doc.setFont('helvetica', 'bold');
      
      // Calculate position (center of page)
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;
      const watermarkText = 'EARLY';
      const textWidth = doc.getTextWidth(watermarkText);
      
      // Draw watermark text centered on page
      doc.text(watermarkText, centerX - textWidth / 2, centerY, { align: 'center' });
      
      // Also add a border around the text for visibility
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(2);
      doc.rect(centerX - textWidth / 2 - 10, centerY - 20, textWidth + 20, 40, 'D');
      
      // Restore original settings
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(currentFontSize);
      doc.setLineWidth(0.5);
    }
  }
  
  // Footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Page ${i} of ${totalPages} - Generated on ${new Date().toLocaleString()}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }
  
  // Generate filename
  const dateStr = new Date(matchInfo.scheduledDate).toISOString().split('T')[0];
  const filename = `Roster_${selectedTeam.name.replace(/\s+/g, '_')}_${dateStr}.pdf`;
  
  doc.save(filename);
};

